import { defineStore } from 'pinia'
import { useQuery } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import { reactive, ref, watch } from 'vue'
import * as IProjects from '@/components/projects/interfaces.ts'
import { type RouteParams } from 'vue-router'
import { isEmpty, isNull, isObject, isString } from 'lodash'
import { ApolloError } from '@apollo/client/core'
import { UILanguage, WebsocketConnectionState } from '@/enums.ts'
import type { AuthorizeUserResponse } from '@/components/users/interfaces.ts'
import { DEFAULT_LANGUAGE } from '@/config/config.ts'

export const useUserStore = defineStore('user', () => {
  const projects = ref<IProjects.Project[]>([])
  const projectSelected = ref<IProjects.Project | null>(null)
  const localeLanguage = ref<UILanguage>(getLocaleLanguage())
  const userInitials = ref<string>('')
  const userFirstName = ref<string>('')
  const userLastName = ref<string>('')
  const userRoleId = ref<number>(-1)
  const wsConnectionState = ref<WebsocketConnectionState>(WebsocketConnectionState.Disconnected)
  let userInfo = reactive<Record<string, unknown> | AuthorizeUserResponse>({})
  const isAuthorized = ref<boolean | null >(null)
  let projectKeyFromUrl: string | null = null

  function authorize (user: AuthorizeUserResponse) {
    userInfo = user

    if (!isNull(user.roleId)) {
      userRoleId.value = user.roleId
    }

    setUserInitialsAndFirstnameLastname(user.firstname, user.lastname)
    isAuthorized.value = true
  }

  function unAuthorize () {
    isAuthorized.value = false
  }

  function setUserInitialsAndFirstnameLastname(firstname: string, lastname: string): void {
    userInitials.value = `${firstname.slice(0, 1)}${lastname.slice(0, 1)}`
    userFirstName.value = `${firstname}`
    userLastName.value = `${lastname}`
  }

  // TODO: TS Pick<>
  function getProjects (): IProjects.Project[] {
    return projects.value
  }

  function setProjects (data: IProjects.Project[]): void {
    projects.value = data
  }

  // Note: Chooses first projects if only single project exists
  function setProjectsAndSelectIfProjectKeyProvidedInUrl (
    data: IProjects.Project[],
    onProjectSelectionFailed: () => void
  ): void {
    setProjects(data)

    if (projects.value.length === 1) {
      setCurrentProject(projects.value[0] as IProjects.Project)
    } else if (isString(projectKeyFromUrl) && !isEmpty(projectKeyFromUrl)) {
      const projectByUrlKey = projects.value.find(({ key }) => {
        return projectKeyFromUrl === key
      })

      if (isObject(projectByUrlKey)) {
        setCurrentProject(projectByUrlKey as IProjects.Project)
      } else {
        onProjectSelectionFailed()
      }
    }

    /* if (isObject(projectByUrlKey) && !isEmpty(projectByUrlKey)) {
      setCurrentProject(projectByUrlKey as IProjects.Project)
    } else {
    }
    if (isUndefined(projectByUrlKey) && isString(projectKeyFromUrl) && !isEmpty(projectKeyFromUrl)) {
      onProjectSelectionFailed()
    } else {
      setCurrentProject(projectByUrlKey as IProjects.Project)
    } */
  }

  function setCurrentProject (project: IProjects.Project): void {
    projectSelected.value = project
  }

  function setCurrentProjectByKey (projectKey: string): void {
    const project = projects.value.find(({ key }) => key === projectKey) ?? null

    if (!isNull(project)) {
      setCurrentProject(project)
    } else {
      console.warn(`user.store.ts | setCurrentProjectByKey(projectKey: ${projectKey}) | unable to find project`)
    }
  }

  function getCurrentProjectKey (): string {
    return isNull(projectSelected.value) ? '' : projectSelected.value!.key
  }

  function setProjectKeyFromRouteParams (params: RouteParams): void {
    if (isString(params.projectKey)) {
      projectKeyFromUrl = params.projectKey
    }
  }

  function setWebsocketConnectionState (state: WebsocketConnectionState) {
    wsConnectionState.value = state
  }

  function getUserInfo (): Record<string, unknown> | AuthorizeUserResponse {
    return userInfo
  }

  function setLocaleLanguage(newLocale: UILanguage): void {
    localeLanguage.value = newLocale
    localStorage.setItem('CROWDY_USER_LANGUAGE', newLocale)
  }

  function getLocaleLanguage(): UILanguage {
    return (localStorage.getItem('CROWDY_USER_LANGUAGE') as UILanguage ?? DEFAULT_LANGUAGE)
  }

  return {
    setProjects,
    getProjects,
    setProjectsAndSelectIfProjectKeyProvidedInUrl,
    setCurrentProject,
    setCurrentProjectByKey,
    setProjectKeyFromRouteParams,
    getCurrentProjectKey,
    setWebsocketConnectionState,
    projects,
    projectSelected,
    isAuthorized,
    authorize,
    unAuthorize,
    localeLanguage,
    setLocaleLanguage,
    userRoleId,
    userInitials,
    userFirstName,
    userLastName,
    wsConnectionState,
    getUserInfo
  }
})
